/**
 * Display formatting.
 *
 * Shared with mobile deliberately: a P&L that renders as `-$1,240.50` on the
 * desktop and `-1240.5` on the phone is the kind of difference that makes a
 * user doubt which number is real.
 *
 * `signGlyph` is not decoration. It is why the market palette is allowed to be
 * close in lightness — the sign is readable with no colour perception at all.
 * See packages/ui/test/semantic.test.ts.
 */
// --- formatting -------------------------------------------------------------
// Display helpers. These are the ONLY place a money string becomes a number,
// and the result is never fed back into arithmetic.

export function fmtUsd(v: string | null | undefined, opts: { sign?: boolean } = {}): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const body = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  if (n < 0) return `-$${body}`;
  return opts.sign ? `+$${body}` : `$${body}`;
}

export function fmtPct(v: string | null | undefined, dp = 1): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${(n * 100).toFixed(dp)}%` : '—';
}

export function fmtNum(v: string | number | null | undefined, dp = 2): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(dp) : '—';
}

/** Sign as a glyph, so meaning is never carried by colour alone. */
export function signGlyph(v: string | null | undefined): string {
  if (v === null || v === undefined) return '';
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '';
  return n > 0 ? '▲' : '▼';
}

export function pnlTone(v: string | null | undefined): 'up' | 'down' | 'flat' {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return 'flat';
  return n > 0 ? 'up' : 'down';
}

/**
 * A timestamp, in a named zone.
 *
 * **`zone` is not decorative.** AEGIS has no default trading style, so the
 * session timezone is a user preference — and a table that renders in the
 * browser's zone while its header names the preference is lying about every
 * row. Pass the same zone the header names, or pass neither.
 *
 * Omitting it keeps the browser's own zone, which is right for things that are
 * about *now* rather than about the trading session: "last seen", "connected
 * at". An invalid zone falls back to the browser's rather than throwing —
 * a stored preference from a renamed IANA zone should not blank a screen.
 */
export function fmtTime(iso: string, zone?: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  };
  const d = new Date(iso);
  if (zone) {
    try {
      return d.toLocaleString('en-US', { ...opts, timeZone: zone });
    } catch {
      return d.toLocaleString('en-US', opts);
    }
  }
  return d.toLocaleString('en-US', opts);
}

export function fmtAgo(epochSeconds: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000 - epochSeconds));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

/**
 * Elapsed time from an ISO timestamp, scaled all the way up to months.
 *
 * `fmtAgo` stops at hours, which is right for a quote that went stale ninety
 * seconds ago and wrong for a device last seen in March — "3400h ago" is a
 * number nobody can read. Used wherever the span can be arbitrarily long.
 */
export function fmtSince(iso: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '—';

  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}h ago`;

  const days = Math.floor(sec / 86_400);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/**
 * How stale the prices are, in the space a badge has.
 *
 * Shared because three surfaces render this and all three had their own
 * arithmetic. The phone said `Delayed 480 min`, which is arithmetically correct
 * and useless — it is the free tier's eight-hour delay, and nobody reads 480
 * minutes as "yesterday's session". The web said `8H DELAYED`, better but still
 * describing a lag rather than what you are looking at.
 *
 * Three bands, because they mean genuinely different things:
 *
 * - **Under an hour** is an exchange delay, and 10 or 15 minutes is exactly how
 *   the exchanges describe their own feeds. Rounding that to hours would lose
 *   the distinction the badge exists for.
 * - **An hour to eight** is a lagging feed, stated in hours.
 * - **Eight hours** is Tier F: not a lagging live feed at all, but the previous
 *   session. Saying so beats any number.
 *
 * Returned in sentence case; a surface that wants shouting can uppercase it.
 */
export function delayLabel(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}-min delayed`;
  const hours = minutes / 60;
  if (hours >= 8) return 'Prior session';
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h delayed`;
}
